/**
 * Putting an evidence file where the rules let it live.
 *
 * Path: orgs/{orgId}/competencyEvidence/{uid}/{assignmentId}/{fileName}
 * (firebase/storage.rules). The member writes it; the member and
 * administrators read it. The URL that comes back is what the evidence
 * record stores.
 */

import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';
import { app } from '../../lib/firebase';

/** The rules cap a file at 25 MB and accept the usual document and image types. */
export const MAX_EVIDENCE_BYTES = 25 * 1024 * 1024;
export const EVIDENCE_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp,.txt,.csv';

const ACCEPTED_TYPES = new Set([
  'application/pdf', 'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp',
  'text/plain', 'text/csv', 'text/html', 'text/markdown',
]);

/** Why a file will not do, before any bytes move. */
export function evidenceFileProblem(file: File): string | null {
  if (file.size === 0) return `${file.name} is empty.`;
  if (file.size > MAX_EVIDENCE_BYTES) return `${file.name} is larger than 25 MB.`;
  if (!ACCEPTED_TYPES.has(file.type)) return `${file.name} is not a document or image type the system accepts.`;
  return null;
}

export function safeFileName(name: string): string {
  return name.replace(/[^\w.\-() ]+/g, '_').slice(0, 255) || 'file';
}

export async function uploadEvidenceFile(orgId: string, uid: string, assignmentId: string, file: File): Promise<string> {
  const path = `orgs/${orgId}/competencyEvidence/${uid}/${assignmentId}/${Date.now()}-${safeFileName(file.name)}`;
  const target = ref(getStorage(app), path);
  await uploadBytes(target, await file.arrayBuffer(), { contentType: file.type });
  return getDownloadURL(target);
}
